import React, { useState, useEffect, useRef, useCallback } from "react";
import Hls from "hls.js";
import {Play, Pause} from "lucide-react";
import "./LazyVideo.css";
import { resolveGuideAssetUrl } from "../../utils/resolveGuideAssetUrl";
import useGuideMediaManifestVersion from "../../hooks/useGuideMediaManifestVersion";

// Hook: true when the container is within 200px of viewport
const useOnScreen = (ref, rootMargin = "200px") => {
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, rootMargin]);

  return isVisible;
};

const LazyVideo = ({
  src,
  forceLoad = false,
  playbackRate = 1,
  width = "100%",
  height = "100%",
  loop = true,
  className = "",
  ui = "minimal",
  clickToToggle = true,
  onReady,
  showLoadingOverlay = true,
  onVisualTransitionEnd,
  onFirstFrame,
}) => {
  useGuideMediaManifestVersion();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const callbacks = useRef({onReady,onFirstFrame});
  callbacks.current = {onReady,onFirstFrame};
  const wantsPlayback = useRef(forceLoad || loop);
  const onScreen = useOnScreen(containerRef);
  const shouldLoad = forceLoad || onScreen;
  const resolvedSrc = resolveGuideAssetUrl(src);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    wantsPlayback.current = true;
    video.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {});
  }, []);

  const pause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    wantsPlayback.current = false;
    video.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (!videoRef.current?.paused && !videoRef.current?.ended) {
      pause();
    } else {
      play();
    }
  }, [pause, play]);

  useEffect(() => {
    setIsReady(false);
    setIsPlaying(false);
    setLoadError(null);
    wantsPlayback.current = forceLoad || loop;
  }, [resolvedSrc, forceLoad, loop]);

  useEffect(() => {if(videoRef.current)videoRef.current.playbackRate=playbackRate;}, [playbackRate,isReady]);

  useEffect(() => {
    if (!shouldLoad || !videoRef.current) return undefined;

    const video = videoRef.current;
    let hlsInstance = null;

    const handleLoadError = () => {
      setLoadError("This guide video is currently unavailable.");
      setIsReady(false);
      setIsPlaying(false);
    };

    const handleReady = () => {
      setLoadError(null);
      setIsReady(true);
      callbacks.current.onReady?.();

      if (wantsPlayback.current) {
        video.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {});
      }
    };

    let hasReportedFirstFrame = false;

    const reportFirstFrame = () => {
      if (hasReportedFirstFrame) return;
      hasReportedFirstFrame = true;
      callbacks.current.onFirstFrame?.();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      reportFirstFrame();
    };

    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      // Some media pipelines emit ended instead of honoring the native loop flag.
      if(loop && wantsPlayback.current){
        video.currentTime=0;
        video.play().then(()=>setIsPlaying(true)).catch(()=>{});
      }
    };

    video.controls = false;
    video.loop = loop;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";

    if (/\.m3u8(?:[?#]|$)/i.test(resolvedSrc) && Hls.isSupported()) {
      hlsInstance = new Hls();
      hlsInstance.loadSource(resolvedSrc);
      hlsInstance.attachMedia(video);
      hlsInstance.on(Hls.Events.MANIFEST_PARSED, handleReady);
      hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) {
          handleLoadError();
        }
      });
    } else {
      video.src = resolvedSrc;
      video.addEventListener("loadeddata", handleReady);
      video.load();
    }

    video.addEventListener("error", handleLoadError);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("playing", reportFirstFrame);

    return () => {
      video.removeEventListener("loadeddata", handleReady);
      video.removeEventListener("error", handleLoadError);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("playing", reportFirstFrame);

      if (hlsInstance) {
        hlsInstance.destroy();
      } else {
        video.removeAttribute("src");
        video.load();
      }
    };
  }, [shouldLoad, resolvedSrc, forceLoad, loop]);

  const handleClick = useCallback(() => {
    const allowAreaClick = ui === "minimal" && clickToToggle;
    if (!allowAreaClick) return;
    toggle();
  }, [ui, clickToToggle, toggle]);

  return (
    <div
      ref={containerRef}
      className={`lazy-video ${className}`.trim()}
      style={{ position: "relative", width, height }}
      onTransitionEnd={onVisualTransitionEnd}
    >
      {shouldLoad && (
        <>
          <video
            ref={videoRef}
            width={width}
            height={height}
            controls={ui === "default" && !loop && isReady}
            autoPlay={forceLoad || loop}
            muted
            playsInline
            loop={loop}
            disablePictureInPicture={loop || ui !== "default"}
            data-ui={ui}
            onClick={handleClick}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {showLoadingOverlay && !isReady && !loadError && (
            <div className="media-loading-badge" aria-live="polite" aria-label="Loading video">
              <span className="media-loading-spinner" aria-hidden="true"></span>
            </div>
          )}

          {loadError && (
            <div
              aria-live="polite"
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                padding: "1rem",
                borderRadius: "inherit",
                background:
                  "linear-gradient(135deg, rgba(21, 157, 181, 0.16), rgba(9, 17, 27, 0.94))",
                color: "var(--clrf7fcfe)",
                fontSize: "0.95rem",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {loadError}
            </div>
          )}

          {(ui === "minimal" || (loop && ui !== "none")) && isReady && !loadError && (
            <div className="lv-minimal-controls">
              <button
                type="button"
                className="lv-pp-btn"
                aria-label={isPlaying ? "Pause" : "Play"}
                title={isPlaying ? "Pause video" : "Play video"}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle();
                }}
              >
                {isPlaying ? <Pause size={17} aria-hidden="true"/> : <Play size={17} aria-hidden="true"/>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LazyVideo;
